<div
     snAssistantSurface="campaign"
     i18n-surfaceTitle="@@campaign.surface"
     surfaceTitle="Outbound campaign" class="outbound-campaign-edit-container">
  <!-- Header -->
  <div class="campaign-edit-header">
    <div class="title-section">
      <h1 i18n="@@campaignEdit.title">Edit Campaign</h1>
      <p class="subtitle" i18n="@@campaignEdit.subtitle">Update campaign settings</p>
    </div>
    <div class="header-actions">
      <p-button
        i18n-label="@@campaignEdit.deleteCampaign"
        label="Delete Campaign"
        icon="pi pi-trash"
        severity="danger"
        [outlined]="true"
        (click)="onDeleteClick()"
        [disabled]="isLoading() || isSaving()"
        snAssistantAction="delete"
        actionDangerous
        actionHint="Deletes the campaign together with its contact list. Calls already made stay in the history."
        class="delete-button-desktop">
      </p-button>
      <p-button
        icon="pi pi-trash"
        severity="danger"
        [outlined]="true"
        (click)="onDeleteClick()"
        [disabled]="isLoading() || isSaving()"
        class="delete-button-mobile">
      </p-button>
    </div>
  </div>

  <!-- Loading -->
  @if (isLoading()) {
    <div class="loading-container">
      <p-progressSpinner class="loading-spinner"></p-progressSpinner>
      <p i18n="@@campaignEdit.loading">Loading campaign...</p>
    </div>
  }

  <!-- Form -->
  @if (!isLoading()) {
    <div class="form-section">
      <p-card>
        <div class="form-content">
          <!-- Campaign Name -->
          <div class="form-field">
            <label class="field-label"><ng-container i18n="@@campaignEdit.name.label">Campaign Name</ng-container> *</label>
            <p class="field-description" i18n="@@campaignCreate.name.description">Give your campaign a descriptive title</p>
            <input
              [snAssistantId]="F.campaignName"
              pInputText
              name="campaign_name"
              [(ngModel)]="campaignName"
              (ngModelChange)="onFieldChange()"
              i18n-placeholder="@@campaignCreate.name.placeholder"
              placeholder="e.g., Customer Outreach Q1 2024"
              class="w-full"
              [disabled]="isSaving()">
          </div>

          <!-- Agent Selection -->
          <div class="form-field">
            <label class="field-label"><ng-container i18n="@@conversations.filter.agent">Agent</ng-container> *</label>
            <p class="field-description" i18n="@@campaignEdit.agent.description">Select the AI agent that will be used for this campaign</p>
            <p-select
              [snAssistantId]="F.agentId"
              name="agent_id"
              [(ngModel)]="selectedAgentId"
              (ngModelChange)="onFieldChange()"
              [options]="agentOptions"
              optionLabel="label"
              optionValue="value"
              i18n-placeholder="@@phoneNumberEdit.selectAgent"
              placeholder="Select an agent"
              [disabled]="isSaving()"
              class="w-full">
            </p-select>

            <a
              [routerLink]="canNavigateToItems() ? ['/outbound-campaigns', campaignId, 'items'] : null"
              [class.disabled-link]="!canNavigateToItems()"
              class="campaign-items-link">
              <i class="pi pi-list"></i> <ng-container i18n="@@campaignEdit.manageItems">Manage Campaign Items</ng-container>
              <span class="items-count-badge">({{ itemsCount }})</span>
              @if (!canNavigateToItems()) {
                <span class="link-hint" i18n="@@campaignEdit.manageItems.hint">(select agent and save changes first)</span>
              }
            </a>
          </div>

          <!-- Phone Number Selection -->
          <div class="form-field">
            <label class="field-label"><ng-container i18n="@@campaignEdit.phoneNumber.label">Phone Number</ng-container> *</label>
            <p class="field-description" i18n="@@campaignEdit.phoneNumber.description">Select the phone number to use for outbound calls</p>
            <p-select
              [snAssistantId]="F.phoneNumberId"
              name="phone_number_id"
              [(ngModel)]="selectedPhoneNumberAgentId"
              (ngModelChange)="onFieldChange()"
              [options]="phoneNumberAgentOptions"
              optionLabel="label"
              optionValue="value"
              i18n-placeholder="@@campaignEdit.phoneNumber.placeholder"
              placeholder="Select a phone number"
              [disabled]="isSaving()"
              class="w-full">
            </p-select>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <div class="action-buttons-left">
              <p-button
                i18n-label="@@common.cancel"
                label="Cancel"
                severity="secondary"
                [outlined]="true"
                (click)="onCancel()"
                [disabled]="isSaving()">
              </p-button>
            </div>
            <div class="action-buttons-right">
              <p-button
                i18n-label="@@common.save"
                label="Save"
                icon="pi pi-check"
                (click)="onSave()"
                [disabled]="!campaignName.trim() || !selectedAgentId || !selectedPhoneNumberAgentId || isSaving()"
                [loading]="isSaving()"
                snAssistantAction="save"
                actionHint="Writes the campaign settings. Field edits do nothing until this runs.">
              </p-button>
              @if (canStart()) {
                <p-button
                  i18n-label="@@campaignEdit.startCampaign"
                  label="Start Campaign"
                  icon="pi pi-play"
                  severity="success"
                  (click)="onStartClick()"
                  [disabled]="isSaving() || isStarting()"
                  [loading]="isStarting()"
                  snAssistantAction="start"
                  actionDangerous
                  actionHint="Starts calling the contact list for real. People will receive calls from the agent — check the agent and the list first.">
                </p-button>
              }
              @if (canStop()) {
                <p-button
                  i18n-label="@@campaignEdit.stopCampaign"
                  label="Stop Campaign"
                  icon="pi pi-pause"
                  severity="danger"
                  (click)="onStopClick()"
                  [disabled]="isSaving() || isStopping()"
                  [loading]="isStopping()"
                  snAssistantAction="stop"
                  actionDangerous
                  actionHint="Stops the campaign. Calls already in progress finish, the rest of the list is not dialled.">
                </p-button>
              }
              @if (canResume()) {
                <p-button
                  i18n-label="@@campaignEdit.resumeCampaign"
                  label="Resume"
                  icon="pi pi-play"
                  severity="success"
                  (click)="onResumeClick()"
                  [disabled]="isSaving() || isStarting()"
                  [loading]="isStarting()"
                  snAssistantAction="resume"
                  actionDangerous
                  actionHint="Resumes a stopped campaign from where it left off.">
                </p-button>
              }
              @if (canRestart()) {
                <p-button
                  i18n-label="@@campaignEdit.restartCampaign"
                  label="Start Over"
                  icon="pi pi-replay"
                  severity="secondary"
                  (click)="onRestartClick()"
                  [disabled]="isSaving() || isStarting()"
                  [loading]="isStarting()"
                  snAssistantAction="restart"
                  actionDangerous
                  actionHint="Starts the whole list over — every contact is called again, including those already reached.">
                </p-button>
              }
            </div>
          </div>
        </div>
      </p-card>
    </div>
  }

  <p-confirmDialog></p-confirmDialog>

  <!-- Delete Confirmation Dialog -->
  <app-delete-confirmation
    [(visible)]="showDeleteDialog"
    [title]="deleteTitle"
    [subtitle]="deleteSubtitle"
    [deleting]="isDeleting()"
    (confirm)="onDeleteConfirm()">
  </app-delete-confirmation>
</div>
